import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { LegacyDraftRowsNotice } from "../components/LegacyDraftRowsNotice";
import { TechFormSectionLoadError } from "../components/TechFormSectionLoadError";
import type { TechFormTablePageProps } from "../models/tech-form-page.models";
import { TECH_FORM_ENDPOINTS } from "../models/tech-form-section-data.constants";
import { TECH_FORM_CACHE_MODES } from "../models/tech-form-vessel-cache.constants";
import {
  TableRow,
  Tq3DraftM,
  mapSqlToTq3DraftForm,
  mapSqlToTq3DraftSummerWinterForm,
  tq3DraftFromInit,
} from "../models/tq3-draft.models";
import { isTechFormSectionDataCached, loadTechFormEndpointData } from "../services/tech-form-section-data.service";
import { tq1VesselDetailsAtom, tq3DraftAtom, tq3DraftTableAtom } from "../state/tech-form.atoms";
import { prepareDraftRowsForEditing } from "../utils/prepare-draft-rows-for-editing.utils";
import {
  DraftTableErrors,
  sanitizeTechFormInputForField,
  validateTq3Draft,
  validateTq3DraftTable,
} from "../utils/tech-form-validations";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export const Tq3Draft: React.FC<TechFormTablePageProps> = ({ isCompletedForm, cacheInitialForm, onSectionReady }) => {
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(tq3DraftAtom);
  const [storedTable, setStoredTable] = useAtom(tq3DraftTableAtom);
  const vesselDetails = useAtomValue(tq1VesselDetailsAtom);

  const { imoNumber, vesselName } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSectionDataReady, setIsSectionDataReady] = useState(false);
  const [sectionLoadError, setSectionLoadError] = useState(false);
  const [sectionLoadAttempt, setSectionLoadAttempt] = useState(0);

  // 2 state store caches
  const [form, setForm] = useState<Tq3DraftM>(tq3DraftFromInit);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof Tq3DraftM, string>>>({});
  const [tableErrors, setTableErrors] = useState<DraftTableErrors>({});
  const [table, setTable] = useState<TableRow[]>(() => {
    return prepareDraftRowsForEditing(storedTable);
  });
  const validateDraftForm = (nextForm: Tq3DraftM) =>
    validateTq3Draft(nextForm, { lightWeight: vesselDetails?.lightWeight });

  useEffect(() => {
    let ignoreResult = false;
    setSectionLoadError(false);
    const _form = storedForm ?? {};
    const _table = storedTable ?? {};
    const storageisEmpty = Object.keys(_form).length === 0 && Object.keys(_table).length === 0;

    if (isCompletedForm) {
      void fetchData(() => ignoreResult);
    } else {
      if (storageisEmpty) {
        void fetchData(() => ignoreResult);
      } else {
        setForm(_form);
        setTable(prepareDraftRowsForEditing(_table));
        cacheInitialForm(_form, _table);
        setIsSectionDataReady(true);
        onSectionReady?.();
      }
    }

    return () => {
      ignoreResult = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imoNumber, vesselName, isCompletedForm, sectionLoadAttempt]);

  useEffect(() => {
    setFormErrors(validateTq3Draft(form, { lightWeight: vesselDetails?.lightWeight }));
    setTableErrors(validateTq3DraftTable(table, { summerSWDraft: form.summerSWDraft }));
  }, [form, table, vesselDetails?.lightWeight]);

  const fetchData = async (isStale: () => boolean) => {
    if (!imoNumber || !vesselName) return;

    try {
      const requestIdentity = {
        imoNumber,
        vesselName,
        mode: isCompletedForm ? TECH_FORM_CACHE_MODES.completed : TECH_FORM_CACHE_MODES.editable,
      };
      if (!isTechFormSectionDataCached({ ...requestIdentity, section: 3 })) {
        setIsLoading(true);
      }
      const [draftRes, draftTableRes] = await Promise.all([
        loadTechFormEndpointData<any[]>({
          ...requestIdentity,
          endpoint: TECH_FORM_ENDPOINTS.vesselDraftSummerWinter,
        }),
        loadTechFormEndpointData<any[]>({ ...requestIdentity, endpoint: TECH_FORM_ENDPOINTS.vesselDraft }),
      ]);
      if (isStale()) return;

      // Form
      const _draftSummwerWinter = draftRes[0];
      const draftSummwerWinter = mapSqlToTq3DraftSummerWinterForm(_draftSummwerWinter);
      setForm(draftSummwerWinter);
      setFormErrors(validateDraftForm(draftSummwerWinter));
      setStoredForm(draftSummwerWinter);

      // Table
      const _draft = draftTableRes;
      const draftMapped = mapSqlToTq3DraftForm(_draft);
      const draftSorted = draftMapped.sort((a: { id: number }, b: { id: number }) => a.id - b.id);
      const nextTable = prepareDraftRowsForEditing(draftSorted);
      setTable(nextTable);
      setTableErrors(validateTq3DraftTable(nextTable));
      setStoredTable(nextTable);
      cacheInitialForm(draftSummwerWinter, nextTable);
      setIsSectionDataReady(true);
      onSectionReady?.();
    } catch (error: unknown) {
      if (!isStale()) {
        setSectionLoadError(true);
        console.error("Unable to load Technical Form draft data", error);
      }
    } finally {
      if (!isStale()) {
        setIsLoading(false);
      }
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeTechFormInputForField(name, value);

    setForm((prevData) => {
      const nextForm = { ...prevData, [name]: sanitizedValue };
      setFormErrors(validateDraftForm(nextForm));
      return nextForm;
    });
  };

  const handleTableInputChange = (id: number, fieldName: string, value: string) => {
    const sanitizedValue = sanitizeTechFormInputForField(fieldName, value);
    const updatedData = table.map((row) => (row.id === id ? { ...row, [fieldName]: sanitizedValue } : row));
    setTable(updatedData);
    setTableErrors(validateTq3DraftTable(updatedData));
  };

  // Storage
  useEffect(() => {
    if (isSectionDataReady) {
      setStoredTable(table);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, isSectionDataReady]);

  useEffect(() => {
    if (isSectionDataReady) {
      setStoredForm(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isSectionDataReady]);

  const inputClass = (error?: string) =>
    twMerge(
      "mt-1 w-full rounded-md border p-2 focus:outline-none",
      error ? "border-red-600" : theme.uwhcAssistant.border,
    );

  const fieldError = (error?: string) => (error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null);

  if (sectionLoadError) {
    return <TechFormSectionLoadError onRetry={() => setSectionLoadAttempt((attempt) => attempt + 1)} />;
  }

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
          theme.balticQuestionnaire.inputsBorder,
          theme.balticQuestionnaire.selectsBg,
          theme.balticQuestionnaire.selectsBorder,
          theme.balticQuestionnaire.textsBg,
          theme.balticQuestionnaire.textsText,
          theme.balticQuestionnaire.textsBorder,
        )}
      >
        <h1 className="custom-heading pb-5">Draft</h1>
        <div>
          <label className="block text-sm font-medium" style={{ color: "#667675" }}>
            Summer draft
          </label>
          <div className="grid grid-cols-5 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium">Sw draft</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="summerSWDraft"
                  value={form.summerSWDraft}
                  onChange={handleFormChange}
                  className={inputClass(formErrors.summerSWDraft)}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  m
                </span>
              </div>
              {fieldError(formErrors.summerSWDraft)}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium">DWT</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="summerDWT"
                  value={form.summerDWT}
                  onChange={handleFormChange}
                  className={inputClass(formErrors.summerDWT)}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  MT
                </span>
              </div>
              {fieldError(formErrors.summerDWT)}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium">TPC</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="summerTPC"
                  value={form.summerTPC}
                  onChange={handleFormChange}
                  className={inputClass(formErrors.summerTPC)}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  m
                </span>
              </div>
              {fieldError(formErrors.summerTPC)}
            </div>
          </div>

          <label className="block text-sm font-medium" style={{ color: "#667675" }}>
            Winter draft
          </label>
          <div className="grid grid-cols-5 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium">Sw draft</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="winterSWDraft"
                  value={form.winterSWDraft}
                  onChange={handleFormChange}
                  className={inputClass(formErrors.winterSWDraft)}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  m
                </span>
              </div>
              {fieldError(formErrors.winterSWDraft)}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium">DWT</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="winterDWT"
                  value={form.winterDWT}
                  onChange={handleFormChange}
                  className={inputClass(formErrors.winterDWT)}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  MT
                </span>
              </div>
              {fieldError(formErrors.winterDWT)}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium">TPC</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="winterTPC"
                  value={form.winterTPC}
                  onChange={handleFormChange}
                  className={inputClass(formErrors.winterTPC)}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  m
                </span>
              </div>
              {fieldError(formErrors.winterTPC)}
            </div>
          </div>

          <div className="relative overflow-x-auto" data-tech-form-field="tq3DraftTable" tabIndex={-1}>
            <label className="block text-sm font-medium" style={{ color: "#667675" }}>
              Please revert 5 consecutive whole numbered (1M apart) smaller drafts, using following format:
              <br /> xx.00m / xxxxxx.xx mt / xx.xx t/cm
            </label>
            <LegacyDraftRowsNotice
              rows={table}
              onRemove={(id) => setTable((current) => current.filter((row) => row.id !== id))}
            />
            <table className="w-50 text-left text-sm">
              <thead className="text-sm font-medium">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    #
                  </th>
                  <th scope="col" className="px-6 py-3">
                    <label>Draft</label>
                  </th>
                  <th scope="col" className="px-6 py-3">
                    <label>DWT</label>
                  </th>
                  <th scope="col" className="px-6 py-3">
                    <label>TPC</label>
                  </th>
                </tr>
              </thead>
              <tbody>
                {table.map((row, index) => (
                  <tr key={row.id}>
                    <th scope="row" className="px-4">
                      <div
                        className={twMerge(
                          "flex items-center justify-center rounded-md border px-3 py-1.5",
                          theme.uwhcAssistant.border,

                          theme.vesselDetails.info,
                        )}
                      >
                        {row.id}
                      </div>
                    </th>
                    <td className="px-6 py-4">
                      <div className="relative flex items-center">
                        <input
                          type="text"
                          name={`tq3DraftTable.${index}.draft`}
                          placeholder="000.00"
                          value={row.draft}
                          onChange={(e) => handleTableInputChange(row.id, "draft", e.target.value)}
                          className={inputClass(tableErrors[row.id]?.draft)}
                        />
                        <span
                          className={twMerge(
                            "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                            theme.uwhcAssistant.border,
                            theme.balticQuestionnaire.grayBg,
                          )}
                        >
                          m
                        </span>
                      </div>
                      {fieldError(tableErrors[row.id]?.draft)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="relative flex items-center">
                        <input
                          type="text"
                          name={`tq3DraftTable.${index}.dwt`}
                          placeholder="000.00"
                          value={row.dwt}
                          onChange={(e) => handleTableInputChange(row.id, "dwt", e.target.value)}
                          className={inputClass(tableErrors[row.id]?.dwt)}
                        />
                        <span
                          className={twMerge(
                            "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                            theme.uwhcAssistant.border,
                            theme.balticQuestionnaire.grayBg,
                          )}
                        >
                          MT
                        </span>
                      </div>
                      {fieldError(tableErrors[row.id]?.dwt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="relative flex items-center">
                        <input
                          type="text"
                          name={`tq3DraftTable.${index}.tpc`}
                          placeholder="000.00"
                          value={row.tpc}
                          onChange={(e) => handleTableInputChange(row.id, "tpc", e.target.value)}
                          className={inputClass(tableErrors[row.id]?.tpc)}
                        />
                        <span
                          className={twMerge(
                            "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                            theme.uwhcAssistant.border,
                            theme.balticQuestionnaire.grayBg,
                          )}
                        >
                          t/cm
                        </span>
                      </div>
                      {fieldError(tableErrors[row.id]?.tpc)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </LoadingWrapper>
  );
};
